import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, fmt, fmtDate, formatApiError, todayISO } from "../lib/api";
import { toast } from "sonner";
import {
  FileText, RefreshCcw, Search, Filter, X, CheckCircle2,
  AlertCircle, ChevronDown, Edit2, Save,
} from "lucide-react";

const FILTERS = [
  { key: "pending",          label: "Pending bill",     color: "bg-[#FEF3C7] text-[#B45309]" },
  { key: "all",              label: "All transactions", color: "bg-gray-100 text-gray-600" },
  { key: "not_required_txn", label: "Not required",     color: "bg-[#DBEAFE] text-[#1D4ED8]" },
  { key: "mismatch",         label: "Amount mismatch",  color: "bg-[#FEE2E2] text-[#B91C1C]" },
];

const PT_FILTERS = [
  { key: "all",      label: "All" },
  { key: "customer", label: "Customers" },
  { key: "vendor",   label: "Vendors" },
];

function BillEditRow({ row, onSaved, onClose }) {
  const [billNo, setBillNo]     = useState(row.bill_no || "");
  const [billAmt, setBillAmt]   = useState(row.bill_amount > 0 ? String(row.bill_amount) : "");
  const [busy, setBusy]         = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const endpoint = row.type === "sale"
        ? `/sales/${row.txn_id}/bill`
        : `/purchases/${row.txn_id}/bill`;
      await api.patch(endpoint, { bill_no: billNo, bill_amount: parseFloat(billAmt) || 0 });
      toast.success("Bill updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="bill-edit-row">
      <input
        autoFocus
        placeholder="Bill No"
        className="input-lg h-8 w-28 text-xs font-mono"
        value={billNo}
        onChange={(e) => setBillNo(e.target.value)}
        data-testid="bill-no-input"
      />
      <input
        type="number"
        placeholder="Bill Amount"
        className="input-lg h-8 w-28 text-xs"
        value={billAmt}
        onChange={(e) => setBillAmt(e.target.value)}
        data-testid="bill-amount-input"
      />
      <button disabled={busy} onClick={save}
        className="btn-primary h-8 px-3 text-xs flex items-center gap-1"
        data-testid="bill-save-btn">
        <Save size={12} /> Save
      </button>
      <button onClick={onClose} className="h-8 px-2 border border-gray-300 rounded-sm text-xs hover:border-black"
        data-testid="bill-cancel-btn">
        <X size={12} />
      </button>
    </div>
  );
}

export default function PendingBillsPage() {
  const [rows, setRows]         = useState([]);
  const [summary, setSummary]   = useState({});
  const [loading, setLoading]   = useState(false);
  const [filter, setFilter]     = useState("pending");
  const [partyType, setPartyType] = useState("all");
  const [search, setSearch]     = useState("");
  const [editId, setEditId]     = useState(null);   // txn_id being edited

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pending-bills", {
        params: { filter, party_type: partyType },
      });
      setRows(data.rows || []);
      setSummary(data.summary || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filter, partyType]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) => r.party_name?.toLowerCase().includes(q)
          || r.challan_no?.toLowerCase().includes(q)
          || r.bill_no?.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const toggleNotRequired = async (row) => {
    try {
      await api.patch(`/sales/${row.txn_id}/bill-not-required`, {
        bill_not_required: !row.bill_not_required_txn,
      });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="pending-bills-page">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <FileText size={22} className="text-black" />
          <h2 className="font-heading font-black text-2xl tracking-tight">Pending Bills</h2>
        </div>
        <span className="text-xs text-gray-500 border border-gray-200 rounded-sm px-2 py-0.5">
          Track challans without a GST bill
        </span>
        <button onClick={load}
          className="ml-auto h-9 px-3 border border-gray-300 rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 hover:border-black"
          data-testid="pb-refresh">
          <RefreshCcw size={13} /> Refresh
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-flat p-5 bg-[#FEF3C7] border-[#FDE68A]">
          <div className="small-label text-[#B45309]">Pending bills</div>
          <div className="data-num text-3xl mt-1 text-[#B45309]" data-testid="pb-pending-count">
            {summary.pending_count ?? "—"}
          </div>
        </div>
        <div className="card-flat p-5 bg-[#FEE2E2] border-[#FECACA]">
          <div className="small-label text-[#B91C1C]">Amount mismatch</div>
          <div className="data-num text-3xl mt-1 text-[#B91C1C]" data-testid="pb-mismatch-count">
            {summary.mismatch_count ?? "—"}
          </div>
        </div>
        <div className="card-flat p-5 bg-[#DBEAFE] border-[#BFDBFE]">
          <div className="small-label text-[#1D4ED8]">Not required (txn)</div>
          <div className="data-num text-3xl mt-1 text-[#1D4ED8]" data-testid="pb-not-required-count">
            {summary.not_required_txn_count ?? "—"}
          </div>
        </div>
        <div className="card-flat p-5">
          <div className="small-label text-gray-500">Total challans</div>
          <div className="data-num text-3xl mt-1" data-testid="pb-total-count">
            {summary.total ?? "—"}
          </div>
        </div>
      </div>

      {/* Filters toolbar */}
      <div className="flex items-center gap-2 flex-wrap card-flat p-3">
        {FILTERS.map((f) => (
          <button key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 h-8 rounded-sm text-xs font-semibold border ${filter === f.key ? "bg-black text-white border-black" : "bg-white text-gray-600 border-gray-300 hover:border-black"}`}
            data-testid={`pb-filter-${f.key}`}>
            {f.label}
          </button>
        ))}
        <div className="h-6 w-px bg-gray-200 mx-1" />
        {PT_FILTERS.map((f) => (
          <button key={f.key}
            onClick={() => setPartyType(f.key)}
            className={`px-3 h-8 rounded-sm text-xs font-semibold border ${partyType === f.key ? "border-black bg-gray-50" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}
            data-testid={`pb-pt-${f.key}`}>
            {f.label}
          </button>
        ))}
        <div className="ml-auto relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search party / challan…"
            className="input-lg h-9 pl-8 w-48 text-sm"
            data-testid="pb-search"
          />
        </div>
      </div>

      {/* Table */}
      <div className="card-flat" data-testid="pb-table">
        <div className="table-wrap">
          <table className="table-dense w-full">
            <thead>
              <tr>
                <th className="text-left">Date</th>
                <th className="text-left">Type</th>
                <th className="text-left">Party</th>
                <th className="text-left">Challan</th>
                <th className="num">Sale ₹</th>
                <th className="text-left">Bill No</th>
                <th className="num">Bill ₹</th>
                <th className="text-left">Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="py-8 text-center text-sm text-gray-400">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-10 text-center text-sm text-gray-400">
                    <CheckCircle2 size={28} className="inline-block text-[#15803D] mb-1.5" />
                    <div>No transactions matching this filter.</div>
                  </td>
                </tr>
              )}
              {!loading && filtered.map((r) => (
                <React.Fragment key={r.txn_id}>
                  <tr data-testid={`pb-row-${r.txn_id}`}>
                    <td className="text-xs text-gray-500">{fmtDate(r.date)}</td>
                    <td>
                      <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-sm ${r.type === "sale" ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-700"}`}>
                        {r.type === "sale" ? "Sale" : "Purchase"}
                      </span>
                    </td>
                    <td>
                      <div className="font-semibold text-sm">{r.party_name || "—"}</div>
                      {r.phone && <div className="text-xs text-gray-400">{r.phone}</div>}
                    </td>
                    <td className="font-mono text-xs">{r.challan_no || "—"}</td>
                    <td className="num data-num font-bold">₹ {fmt(r.sale_amount)}</td>
                    <td className="font-mono text-xs">
                      {r.has_bill ? (
                        <span className="inline-flex items-center gap-1 text-[#15803D] font-semibold">
                          <CheckCircle2 size={12} /> {r.bill_no}
                        </span>
                      ) : r.bill_not_required_txn ? (
                        <span className="text-[#1D4ED8] text-[11px]">Not req.</span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="num text-xs">
                      {r.bill_amount > 0 ? (
                        <span className={r.bill_mismatch ? "text-[#B91C1C] font-bold" : ""}>
                          ₹ {fmt(r.bill_amount)}
                          {r.bill_mismatch && " ⚠"}
                        </span>
                      ) : "—"}
                    </td>
                    <td>
                      {r.has_bill && !r.bill_mismatch && (
                        <span className="inline-flex items-center gap-1 text-xs text-[#15803D]">
                          <CheckCircle2 size={11} /> Billed
                        </span>
                      )}
                      {r.bill_mismatch && (
                        <span className="inline-flex items-center gap-1 text-xs text-[#B91C1C] font-semibold">
                          <AlertCircle size={11} /> Mismatch
                        </span>
                      )}
                      {!r.has_bill && !r.bill_not_required_txn && !r.bill_not_required_party && (
                        <span className="inline-flex items-center gap-1 text-xs text-[#B45309]">
                          <AlertCircle size={11} /> Pending
                        </span>
                      )}
                    </td>
                    <td className="text-right">
                      <div className="inline-flex items-center gap-1">
                        {!r.bill_not_required_txn && (
                          <button
                            onClick={() => setEditId(editId === r.txn_id ? null : r.txn_id)}
                            className="h-7 px-2 border border-gray-300 rounded-sm text-xs hover:border-black inline-flex items-center gap-1"
                            data-testid={`pb-edit-${r.txn_id}`}>
                            <Edit2 size={11} /> {r.has_bill ? "Edit" : "Add Bill"}
                          </button>
                        )}
                        {r.type === "sale" && (
                          <button
                            onClick={() => toggleNotRequired(r)}
                            className="h-7 px-2 border border-gray-200 rounded-sm text-[10px] hover:border-gray-400"
                            title={r.bill_not_required_txn ? "Mark bill needed" : "Mark bill not required"}
                            data-testid={`pb-flag-${r.txn_id}`}>
                            <ChevronDown size={11} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {editId === r.txn_id && (
                    <tr>
                      <td colSpan={9} className="px-4 pb-3 bg-gray-50">
                        <BillEditRow
                          row={r}
                          onSaved={() => { setEditId(null); load(); }}
                          onClose={() => setEditId(null)}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
